import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { get as _get, debounce as _debounce } from 'lodash';
import {
    format as D3format,
    scaleLinear as D3scaleLinear,
} from 'd3';
import Moment from 'moment';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import Auth, { ParamsSerializer } from '../../../../data/auth-service';
import DashboardService from '../../../../data/dashboard-service';
import MetadataService from '../../../../data/metadata-service';
import DataContext from '../../../../data/data-context.js';

import SparklineGrid, { CalcVisibleSeries } from './../components/responsive-sparkline-grid';
import SourceSelector from './../components/source-selector';
import DateRange from './../components/date-range';

import { TagSimpleList } from '../../../../components/tags/taglist';
import Tag from '../../../../components/tags/tag';
import FilterBuilder from '../../../../components/filter/filterbuilder';
import { GetISODateFromQuick } from '../../../../libraries/helpers';
import { lavender } from '../../../../data/colors';
import { VerifyDates } from '../../widget';

const useStyles = makeStyles(theme => ({
    root: {
    },
    label: {
        width: '100%',
        justifyContent: 'flex-end',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    taglabel: {
        paddingLeft: 5,
        position: 'relative',
        top: -5,
    },
    bar: {
        display: 'inline-block',
        marginTop: 0,
        height: 7,
        position: 'relative',
        top: -7,
    },
    bars: {
        listStyleType: 'none',
        paddingLeft: '0',
        paddingTop: 14,
    },
    // editor classes
    editorRoot: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    tagsButton: {
        width: 99,
    },
    filterText: {
        marginLeft: 10,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    info: {
        ...theme.typography.body2,
        opacity: 0.7,
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 14 }
    : { minW: 5, minH: 5 };

// don't display until size-me can figure out what our size is
const MtdtActiveTagsWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
MtdtActiveTagsWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const Widget = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();

    const [data, setData] = useState([{ metrics: [], timeline: [] }]);
    const [max, setMax] = useState(() => 0);
    const [height, setHeight] = useState(0);
    const [limit, setLimit] = useState(0);
    const [, setErrors] = useState(0);
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const [settings] = useState({
        margin: 0,
        labelWidth: 0,
        chartHeight: 40,
        scale: 'linear',
        vgrid: true,
        hgrid: true,
    });

    const mdPerms = Auth.permissions.get('Metadata');

    const barScale = () => D3scaleLinear()
        .range([0, 60])
        .domain([0, max || 0]);

    const fetch = controller => {
        props.setBusy(true);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        if (mdPerms.read) {
            return DashboardService.getMtdtTimelineGroups({
                start,
                end,
                filter: props.data.settings.json_filter,
                group: ['bricata.tag'],
                source: props.data.settings.types,
                tags: props.data.settings.tags,
                order: props.data.settings.order,
                tags_op: 'Or',
                limit,
            }, controller).catch(err => {
                props.onError(err, props.data.title);
                setErrors(current => {
                    props.setBusy(false, current > 2);
                    if (current > 2) DashboardService.unsubscribe(props.data.id);
                    return current + 1;
                });
                return 'error';
            });
        }
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);

        // the widget expects a single timeline. need to merge the Data
        let max = 0;
        const timeline = {};
        const series = (res || []).reduce((a, c) => {
            // update timeline map
            _get(c, 'timeline.aggregations.timeline.buckets', []).forEach(t => {
                if (!timeline[t.key_as_string]) {
                    timeline[t.key_as_string] = {
                        doc_count: t.doc_count,
                        timestamp: t.key_as_string,
                        series: {}
                    };
                }
                timeline[t.key_as_string].doc_count += t.doc_count;
                timeline[t.key_as_string].series[c.value] = t.doc_count;
            });

            // update max group
            max = Math.max(max, c.count);

            // set current series
            return {
                ...a,
                [c.value]: { key: c.value, value: d => d.series[c.value], count: c.count },
            };
        }, {});

        const da = [{
            index: 0,
            key: 'tag',
            metrics: Object.values(series).sort((a, b) => {
                if (a.count === b.count) {
                    return a.key < b.key ? -1 : 1;
                }
                return props.data.settings.order === 'Least'
                    ? a.count - b.count
                    : b.count - a.count;
            }),
            timeline: Object.values(timeline).sort((a, b) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        }];

        setHeight(da[0].metrics.length * settings.chartHeight);
        setData(da);
        setMax(max);
    };

    const subscribe = () => {
        setErrors(0);
        // truncate the data if we need to
        if (data[0].metrics.length > limit) {
            const d = data[0];
            d.metrics = d.metrics.slice(0, limit);
            setData([d]);
            setHeight(d.metrics.length * settings.chartHeight);
        }

        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const handleGroupClick = g => {
        const f = {
            operator: 'And',
            nodes: [{ variable: 'bricata.tag', value: `${g}` }],
        };
        if (_get(props.data, 'settings.json_filter.nodes', []).length > 0) {
            f.nodes.push(props.data.settings.json_filter);
        }
        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: JSON.stringify(f),
            sources: props.data.settings.types,
            search: props.data.settings.search,
            tags: props.data.settings.tags,
            tags_op: 'Or',
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    };

    const handleSparklineClick = useCallback((d, k) => {
        if (!d || !k) return;
        const f = {
            operator: 'And',
            nodes: [{ variable: 'bricata.tag', value: `${k}` }],
        };
        if (_get(props.data, 'settings.json_filter.nodes', []).length > 0) {
            f.nodes.push(props.data.settings.json_filter);
        }
        const p = {
            start: d.timestamp,
            end: Moment(d.timestamp).add(10, 'm').utc().toISOString(),
            filter: JSON.stringify(f),
            sources: props.data.settings.types,
            search: props.data.settings.search,
            tags: props.data.settings.tags,
            tags_op: 'Or',
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    }, [navigate, props.data]);

    // we only need one debouncer
    const setLimitDebounce = useMemo(() => _debounce(setLimit, 420), []);

    // incoming
    useEffect(() => {
        setErrors(0);
        if (!_get(props.size, 'height', 0)) return;
        setLimitDebounce(Math.min(CalcVisibleSeries(props.size.height, 60, settings.chartHeight), 42));
    }, [props.size, settings.chartHeight, setLimitDebounce]);

    useEffect(() => {
        setErrors(0);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, [props.data.id]);

    // widget inited
    useEffect(() => {
        if (!limit) return;
        subscribe();
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
    }, [limit, props.data.start, props.data.end, props.data.quick]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            <Grid container
                spacing={2}
                direction="row"
                wrap='nowrap'
                justifyContent="space-between"
                style={{ width: 'calc(100% - 20px)', height: props.size.height }}>

                <Grid item style={{ maxWidth: 150 }}>
                    <div style={{ marginTop: 20 }}>
                        {data[0].metrics.map((m, i) => (
                            <Button
                                key={`${m.key}_${i}`}
                                className={classes.label}
                                component="button"
                                onClick={() => handleGroupClick(m.key)}
                                aria-label="tag link">
                                <Tag tag={m.key} noclick={true}/>
                            </Button>
                        ))}
                    </div>
                </Grid>

                <SparklineGrid
                    metrics={data}
                    height={height}
                    width={props.size.width - 250}
                    start={start}
                    end={end}
                    settings={settings}
                    onClick={handleSparklineClick}
                    key={props.data.id}>
                </SparklineGrid>

                <Grid style={{ maxWidth: 100 }}>
                    <div style={{ marginTop: 20, maxWidth: 100 }}>
                        <ul className={classes.bars}>
                            {data[0].metrics.map((m, i) => (
                                <li key={`${m.key}_${i}`} style={{ paddingBottom: 0 }}>
                                    <div>
                                        <div>{D3format(',')(m.count)}</div>
                                        <div className={classes.bar}
                                            style={{
                                                width: barScale()(m.count) || 5,
                                                backgroundColor: lavender[400],
                                            }}>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Grid>

            </Grid>
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const tagbtnRef = useRef(null);
    const daForm = useValidate({});
    const { assets, sensorNames } = useContext(DataContext);

    const [fields, setFields] = useState([]);
    const [title, setTitle] = useState(props.data.title);
    const [filter, setFilter] = useState(props.data.settings.json_filter);
    const [order, setOrder] = useState(props.data.settings.order || 'Most');
    const [tags, setTags] = useState(props.data.settings.tags || []);
    const [sources, setSources] = useState(props.data.settings.types || ['conn']);
    const [showTags, setShowTags] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const mdPerms = Auth.permissions.get('Metadata');

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        daForm.set({ ...daForm.get(), [validID]: valid });
        setTitle(e.target.value);
    };

    const handleOrderChange = e => setOrder(e.target.value);

    const handleFilterChange = ({ filter }) => {
        setFilter(filter);
        setShowFilter(false);
    };

    const handleRemoveTag = t => {
        const i = tags.findIndex(tag => t === tag);
        if (i >= 0) tags.splice(i, 1);
        setTags([...tags]);
    };

    const handleAddTag = t => {
        const i = tags.findIndex(tag => t.name === tag);
        if (i < 0) tags.push(t.name);
        setTags([...tags]);
    };

    const handleRemoveSource = s => {
        const i = sources.findIndex(src => s === src);
        if (i >= 0) sources.splice(i, 1);
        setSources([...sources]);
    };

    const handleAddSource = s => {
        const i = sources.findIndex(src => s === src);
        if (i < 0) sources.push(s);
        setSources([...sources]);
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;
        const formState = daForm.get() || {};
        if ((Object.keys(formState).length === 0 && title && title.trim().length > 0) && (sources.length > 0)) {
            return true;
        }
        if (sources.length < 1) return false;
        return checkValidity(formState);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                json_filter: filter,
                types: sources,
                tags,
                order,
            },
        });
    };

    const fetchFields = async () => {
        if (mdPerms.read) {
            const fields = await MetadataService.cacheFields();
            setFields(fields.filter(c => c.indexed && c.type !== 'timestamp'));
        }
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    <TextField
                        select
                        label="Order"
                        aria-label="Order"
                        value={order}
                        onChange={handleOrderChange}>
                        {['Most', 'Least'].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    {props.data.date_override &&
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    }
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <FilterBuilder
                                onOK={handleFilterChange}
                                onShow={setShowFilter}
                                show={showFilter}
                                fields={fields}
                                filter={filter}
                                placement="right-start"
                                button={
                                    <Button
                                        className={classes.tagsButton}
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AccountTreeIcon/>}>Filter
                                    </Button>
                                }/>
                            <Typography
                                variant="body2"
                                component="span"
                                className={classes.filterText}>
                                {ReactHtmlParser(MetadataService.toEnglish(filter, true, sensorNames, assets))}
                            </Typography>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <SourceSelector
                                onAdd={handleAddSource}
                                onRemove={handleRemoveSource}
                                sources={sources}
                                container={tagbtnRef.current}
                                button={
                                    <Button
                                        aria-label="add source"
                                        title="add source"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AddIcon/>}>Source
                                    </Button>
                                }/>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-start" direction="column">
                        <Grid item>
                            <div className={classes.info}>Limit results to these tags:</div>
                        </Grid>
                        <Grid item>
                            <Button
                                ref={tagbtnRef}
                                onClick={() => setShowTags(true)}
                                aria-label="filter results by tag"
                                title="filter results by tag"
                                variant="contained"
                                color="primary"
                                size="small"
                                className={classes.tagsButton}
                                startIcon={<BookmarkIcon />}> Tags
                            </Button>
                            {tags.map((t, i) =>
                                <div key={`${t}_i`} className={classes.inlineTag}>
                                    <Tag
                                        onUntag={handleRemoveTag}
                                        untagLabel={'remove'}
                                        tag={t}/>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </div>
            </Box>
            <Box>
                <TagSimpleList
                    show={showTags}
                    placement='bottom-end'
                    onClickAway={() => setShowTags(false)}
                    anchorEl={tagbtnRef.current}
                    onSelect={handleAddTag}/>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="secondary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    MtdtActiveTagsWidget as default,
    DEFAULTS,
};
